import { LabFeatureFlags } from 'constants/lab'
import { useLabFeatureFlag } from 'hooks/FeatureFlag/useLabFeatureFlag'
import useIsSearchPage from 'hooks/Navigation/useIsSearchPage'
import useIsHomePage from 'hooks/Navigation/useIsHomePage'
import useIsOfferPage from 'hooks/Navigation/useIsOfferPage'
import { useContext, useMemo } from 'react'
import SiteTakeoverPopup from 'takeovers/utils/components/SiteTakeoverPopup'
import { useShouldShowBanner } from 'takeovers/utils/hooks/useShouldShowBanner'
import { isLabTakeoverBannerContentPayload } from 'lib/lab/dynamicPayload'
import useAgentHubEarnYourOwnTourLoginModal from 'agentHub/hooks/useAgentHubEarnYourOwnTourLoginModal'
import useAgentHubSocieteLoginPrompt from 'agentHub/hooks/useAgentHubSocieteLoginPrompt'
import PageBannerCarousel from 'LuxKit/Banners/PageBannerCarousel'
import type { PageBannerCarouselItem } from 'LuxKit/Banners/types'
import useNotificationBannerItems from './HeaderBanners/useNotificationBannerItem'
import useLabV2HeaderBannerItems from './HeaderBanners/useLabV2HeaderBannerItems'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import useLuxPlusWarningItem from 'luxPlus/components/Banners/useLuxPlusWarningItem'
import useLuxPlusRenewalBannerItem from 'luxPlus/components/Banners/useLuxPlusRenewalBannerItem'
import useLoyaltyActivationBannerItem from 'loyalty/components/Banners/useLoyaltyActivationBannerItem'
import useAtolBannerItem from './HeaderBanners/useAtolBannerItem'
import useAgentHubBannerItems from './HeaderBanners/useAgentHubBannerItems'
import useCookiesBannerItem from './HeaderBanners/useCookiesBannerItem'
import useSiteTakeoverBannerItem from 'takeovers/utils/components/useSiteTakeoverBannerItem'
import useLuxPlusFlightVoucherOfferBannerItem from 'luxPlus/components/Banners/useLuxPlusFlightVoucherOfferBannerItem'
import { useAppSelector } from 'hooks/reduxHooks'
import GeoContext from 'contexts/geoContext'
import { removeRegionCode } from 'lib/string/regionUtils'
import { isMetaSearchPage } from 'components/Search/utils'
import { getTakeoverBannerLocation } from './HeaderBanners/NotificationBannerV3.utils'
import { useIsBannerHidden } from 'hooks/useIsBannerHidden'

const METASEARCH_MOBILE_BANNER_ALLOW_LIST = ['atol-protected', 'luxplus-warning'] as const

export function getHeaderTopBannerItems({
  notificationItems,
  labV2Items = [],
  fallbackItems,
  takeoverItem,
  shouldShowFallbackItems,
  allowList,
}: {
  notificationItems: Array<PageBannerCarouselItem>
  labV2Items?: Array<PageBannerCarouselItem>
  fallbackItems: Array<PageBannerCarouselItem | null>
  takeoverItem: PageBannerCarouselItem | null
  shouldShowFallbackItems: boolean
  allowList?: ReadonlyArray<string>
}): Array<PageBannerCarouselItem> {
  const items = [
    ...notificationItems,
    ...labV2Items,
    ...(shouldShowFallbackItems ? fallbackItems : []),
    takeoverItem,
  ].filter((item): item is PageBannerCarouselItem => !!item)

  return allowList ? items.filter(item => allowList.includes(item.id)) : items
}

export function getShouldShowFallbackBannerItems({
  currentPath,
  headerBannersLoadedPath,
  shouldShowNotificationTakeoverBanner,
}: {
  currentPath: string
  headerBannersLoadedPath?: string
  shouldShowNotificationTakeoverBanner: boolean
}): boolean {
  return headerBannersLoadedPath === currentPath || shouldShowNotificationTakeoverBanner
}

/**
 * Contains all the banners that are meant to be displayed on top of the page.
 */
function HeaderTopBanners() {
  const hideBanners = useIsBannerHidden('headerTopBanners')
  const { currentRegionCode } = useContext(GeoContext)
  const { items: notificationItems, inlineCtaItemId } = useNotificationBannerItems()
  const labV2Items = useLabV2HeaderBannerItems()
  const isMobile = useScreenSizeOnly('mobile')
  const warningItem = useLuxPlusWarningItem()
  const renewalItem = useLuxPlusRenewalBannerItem()
  const loyaltyItem = useLoyaltyActivationBannerItem()
  const atolItem = useAtolBannerItem()
  const agentHubItems = useAgentHubBannerItems()
  const cookiesItem = useCookiesBannerItem()
  const takeoverItem = useSiteTakeoverBannerItem()
  const flightVoucherItem = useLuxPlusFlightVoucherOfferBannerItem()
  const pathname = useAppSelector(state => state.router.location.pathname)
  const allowList = isMobile && isMetaSearchPage(pathname) ? METASEARCH_MOBILE_BANNER_ALLOW_LIST : undefined
  const headerBannersLoadedPath = useAppSelector(state => state.system.features.headerBannersLoadedPath)
  const currentPath = useMemo(() => removeRegionCode(pathname, currentRegionCode), [pathname, currentRegionCode])
  const isSearchPage = useIsSearchPage()
  const isOfferPage = useIsOfferPage()
  const notificationTakeoverBannerLocation = getTakeoverBannerLocation(isSearchPage, isOfferPage)
  const shouldShowNotificationTakeoverBanner = useShouldShowBanner({ location: notificationTakeoverBannerLocation })
  const shouldShowFallbackItems = getShouldShowFallbackBannerItems({
    currentPath,
    headerBannersLoadedPath,
    shouldShowNotificationTakeoverBanner,
  })

  const fallbackItems = useMemo(() => [
    warningItem,
    renewalItem,
    loyaltyItem,
    atolItem,
    ...agentHubItems,
    cookiesItem,
    flightVoucherItem,
  ], [
    warningItem,
    renewalItem,
    loyaltyItem,
    atolItem,
    agentHubItems,
    cookiesItem,
    flightVoucherItem,
  ])

  const carouselItems = useMemo(() => getHeaderTopBannerItems({
    notificationItems,
    labV2Items,
    fallbackItems,
    takeoverItem,
    shouldShowFallbackItems,
    allowList,
  }), [
    notificationItems,
    labV2Items,
    fallbackItems,
    takeoverItem,
    shouldShowFallbackItems,
    allowList,
  ])

  // On mobile, when the LuxPlus+ Month inline-CTA banner is the only banner showing (nothing else
  // to slide between), collapse the carousel's reserved mobile-controls height down to its content
  // — there's no pagination/hidden-button space to reserve for a carousel of one.
  const shouldCollapseToContent = isMobile &&
    carouselItems.length === 1 &&
    carouselItems[0].id === inlineCtaItemId

  const displayedCarouselItems = useMemo(() => {
    if (!shouldCollapseToContent) return carouselItems
    return [{ ...carouselItems[0], hideMobileActionButton: true }]
  }, [carouselItems, shouldCollapseToContent])

  const enableTakeoverBannerContent = useLabFeatureFlag(LabFeatureFlags.enableTakeoverBannerContent)
  const isHomePage = useIsHomePage()
  const shouldShowGenericBanner = useShouldShowBanner({ location: 'generic' })

  const bannerContentPayload: App.LabTakeoverBannerContentPayload | undefined = useMemo(() => {
    if (enableTakeoverBannerContent?.enabled) {
      const payload = enableTakeoverBannerContent.value
      if (isLabTakeoverBannerContentPayload(payload)) {
        return payload
      }
    }
    return undefined
  }, [enableTakeoverBannerContent])

  useAgentHubSocieteLoginPrompt()
  useAgentHubEarnYourOwnTourLoginModal()

  const showPopup = !isHomePage && !isSearchPage && shouldShowGenericBanner &&
    (bannerContentPayload?.genericBannerType === 'takeoverpopup' || bannerContentPayload?.genericBannerType === 'whitepopup')

  if (hideBanners) return null

  return <div className="no-print">
    {displayedCarouselItems.length > 0 && <PageBannerCarousel items={displayedCarouselItems} />}
    {showPopup && <SiteTakeoverPopup />}
  </div>
}

export default HeaderTopBanners
